/**
 * Sesión del POS: tokens, refresco automático y cierre.
 *
 * Tres cosas que un POS necesita y que un cliente HTTP normal no hace:
 *
 * 1. **Persistencia.** La caja se recarga (o se reinicia el navegador) y el
 *    cajero no puede volver a teclear su contraseña cada vez.
 * 2. **Refresco automático.** El token de acceso dura 15 minutos; el turno dura
 *    todo el día. Cuando una petición recibe un 401, se renueva el token y se
 *    reintenta **una sola vez**, sin que la pantalla se entere.
 * 3. **Un solo refresco a la vez.** Si cinco peticiones fallan a la vez con 401,
 *    no se lanzan cinco refrescos: se comparte la misma promesa. Cinco refrescos
 *    simultáneos con rotación de tokens harían que cuatro parecieran robados y
 *    el servidor cerraría la sesión —un bug difícil de reproducir y peor de
 *    diagnosticar.
 */

import type { components } from "./api-types";

type Schemas = components["schemas"];
export type UserPublic = Schemas["UserPublic"] & { is_superadmin?: boolean };
export type TenantPublic = Schemas["TenantPublic"] & {
  legal_name?: string | null;
  rfc?: string | null;
  tax_regime?: string | null;
  fiscal_postal_code?: string | null;
  prices_include_tax?: boolean;
  business_day_cutoff_hour?: number;
  suggested_tip_percent?: number;
  has_pos?: boolean;
  has_restaurant?: boolean;
  has_workshop?: boolean;
  settings?: Record<string, unknown> | null;
  schema_mode?: "shared" | "dedicated";
  schema_name?: string | null;
};
export type SessionResponse = Omit<Schemas["SessionResponse"], "user" | "tenant"> & {
  user: UserPublic;
  tenant: TenantPublic;
};
export type BranchPublic = Schemas["BranchPublic"];

const STORAGE_KEY = "megasyr.session";

export interface StoredSession {
  tokens: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    refresh_expires_at: string;
  };
  user: UserPublic;
  tenant: TenantPublic;
  roles: string[];
  permissions: string[];
  branches: BranchPublic[];
  /** Momento (epoch ms) en que caduca el token de acceso. */
  expiresAt: number;
}

let current: StoredSession | null = readFromStorage();
const listeners = new Set<(session: StoredSession | null) => void>();

function readFromStorage(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    // Si el token de refresco ya caducó, la sesión no sirve de nada.
    if (new Date(parsed.tokens.refresh_expires_at).getTime() < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persist(session: StoredSession | null): void {
  if (session) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  for (const listener of listeners) listener(session);
}

export function getSession(): StoredSession | null {
  return current;
}

export function setSession(payload: Omit<SessionResponse, "tokens"> & { tokens: SessionResponse["tokens"] }): StoredSession {
  const session: StoredSession = {
    ...payload,
    expiresAt: Date.now() + payload.tokens.expires_in * 1000,
  };
  current = session;
  persist(session);
  return session;
}

export function updateSessionTenant(partialTenant: Partial<TenantPublic>): StoredSession | null {
  if (!current) return null;
  const updated: StoredSession = {
    ...current,
    tenant: {
      ...current.tenant,
      ...partialTenant,
      settings: partialTenant.settings
        ? { ...(current.tenant.settings ?? {}), ...partialTenant.settings }
        : current.tenant.settings,
    },
  };
  current = updated;
  persist(updated);
  return updated;
}

export function clearSession(): void {
  current = null;
  persist(null);
}

export function onSessionChange(listener: (session: StoredSession | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function hasPermission(permission: string): boolean {
  return current?.permissions.includes(permission) ?? false;
}

// ---------------------------------------------------------------------------
// Refresco con una sola petición en vuelo
// ---------------------------------------------------------------------------

let refreshInFlight: Promise<StoredSession | null> | null = null;

/**
 * Renueva el token de acceso. Devuelve la sesión nueva o `null` si no se pudo.
 *
 * La promesa se comparte: si llegan diez peticiones con 401 a la vez, se hace
 * **un** refresco y las diez esperan el mismo resultado.
 */
export async function refreshSession(baseUrl: string): Promise<StoredSession | null> {
  if (refreshInFlight) return refreshInFlight;

  const session = current;
  if (!session) return null;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${baseUrl}/api/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: session.tokens.refresh_token }),
      });
      if (!response.ok) {
        clearSession();
        return null;
      }
      const payload = (await response.json()) as SessionResponse;
      return setSession(payload);
    } catch {
      // Sin red: la sesión sigue siendo válida para trabajar offline.
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/** ¿El token de acceso está a punto de caducar? */
export function accessTokenExpiring(thresholdMs = 60_000): boolean {
  if (!current) return false;
  return current.expiresAt - Date.now() < thresholdMs;
}
